import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

function MaterialCompositesDetail() { // ✅ Updated component name
  const { shipmentId } = useParams();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchShipmentDetails = async () => {
      try {
        const compositesRef = collection(db, 'material_composites'); // ✅ Updated collection
        const q = query(compositesRef, where("ID Number", "==", shipmentId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const shipmentDoc = querySnapshot.docs[0];
          setShipment({ id: shipmentDoc.id, ...shipmentDoc.data() });
        } else {
          setError('Material Composite record not found');
        }
      } catch (err) {
        console.error('Error fetching composite details:', err);
        setError('Failed to load material composite details');
      } finally {
        setLoading(false);
      }
    };

    fetchShipmentDetails();
  }, [shipmentId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button
          component={Link}
          to="/material-composites" // ✅ Updated path
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none' }}
        >
          Back to Material Composites List
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          component={Link}
          to="/material-composites" // ✅ Updated path
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none' }}
        >
          Back to Material Composites List
        </Button>
        <Typography variant="h4" component="h1">
          Material Composite Details
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  ID Number
                </Typography>
                <Typography variant="body1">{shipment["ID Number"] || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Collection Point
                </Typography>
                <Typography variant="body1">{shipment["Collection Point"] || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Material Type
                </Typography>
                <Typography variant="body1">{shipment["Specific Material"] || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Typography variant="body1">{shipment["Next Step"] || "N/A"}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Weight Measurements
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Wet Weight
                </Typography>
                <Typography variant="body1">{shipment["Wet Weight (g)"] || "N/A"} g</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Dry Weight
                </Typography>
                <Typography variant="body1">{shipment["Dry weight (g)"] || "N/A"} g</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Dates
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Collection Date
                </Typography>
                <Typography variant="body1">{shipment["Collection Date"] || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Processing Start Date
                </Typography>
                <Typography variant="body1">{shipment["Processing Start Date"] || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Processing End Date
                </Typography>
                <Typography variant="body1">{shipment["Processing End Date"] || "N/A"}</Typography>
              </Grid>
            </Grid>

            {shipment["Notes"] && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body1">{shipment["Notes"]}</Typography>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              QR Code
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              {shipment.qrCodeValue ? (
                <QRCodeCanvas value={shipment.qrCodeValue} size={200} />
              ) : (
                <Typography color="text.secondary">No QR code available</Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default MaterialCompositesDetail; // ✅ Updated export
